import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/ui-helpers";
import { ArrowLeft, Phone, Mail, Send, MapPin, IdCard } from "lucide-react";
import { initials, prettyDate, timeAgo } from "@/mobile/mobileUtils";
import { toast } from "sonner";

export default function MobileClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [tab, setTab] = useState("profile");
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadMsgs = () => api.get(`/messages?client_id=${id}`).then((r) => setMsgs(r.data || [])).catch(() => {});
  useEffect(() => {
    api.get(`/clients/${id}`).then((r) => setClient(r.data)).catch(() => setClient(false));
    loadMsgs();
  }, [id]);
  useEffect(() => { if (tab === "chat") endRef.current?.scrollIntoView(); }, [tab, msgs]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try { await api.post("/messages", { client_id: id, body }); setText(""); await loadMsgs(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  if (client === null) return <Loader />;
  if (client === false) return <div className="p-6 text-center text-slate-400">Client not found.</div>;

  const fields = [
    { icon: Phone, label: "Phone", value: client.phone, href: client.phone ? `tel:${client.phone}` : null },
    { icon: Mail, label: "Email", value: client.email, href: client.email ? `mailto:${client.email}` : null },
    { icon: MapPin, label: "Address", value: client.address },
    { icon: IdCard, label: "UCI", value: client.uci },
    { icon: IdCard, label: "Passport", value: client.passport_no },
  ].filter((f) => f.value);

  return (
    <div className="pb-4" data-testid="mobile-client-detail">
      <div className="bg-gradient-to-br from-secondary to-[#123a63] text-white px-4 pt-3 pb-5">
        <button onClick={() => nav("/m/clients")} data-testid="client-back" className="mb-3 text-white/80"><ArrowLeft className="h-5 w-5" /></button>
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 rounded-full bg-white/15 font-bold flex items-center justify-center text-lg">{initials(client.full_name)}</div>
          <div className="min-w-0">
            <div className="font-head font-extrabold text-xl truncate">{client.full_name}</div>
            <div className="text-xs text-slate-300">{client.immigration_status || client.client_type || "Client"}</div>
          </div>
        </div>
      </div>

      <div className="flex border-b border-slate-100 bg-white sticky top-14 z-10">
        {["profile", "chat"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`client-tab-${t}`}
            className={`flex-1 py-3 text-sm font-semibold capitalize ${tab === t ? "text-primary border-b-2 border-primary" : "text-slate-400"}`}>
            {t === "chat" ? "Messages" : "Profile"}
          </button>
        ))}
      </div>

      {tab === "profile" ? (
        <div className="p-4 space-y-2">
          {fields.map((f, i) => (
            <a key={i} href={f.href || undefined} className={`flex items-center gap-3 bg-white rounded-2xl p-3.5 border border-slate-100 ${f.href ? "active:bg-slate-50" : ""}`} data-testid={`client-field-${f.label.toLowerCase()}`}>
              <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><f.icon className="h-4 w-4" /></div>
              <div className="min-w-0"><div className="text-[11px] text-slate-400 uppercase tracking-wide">{f.label}</div><div className="text-sm font-medium text-slate-800 break-words">{f.value}</div></div>
            </a>
          ))}
          {(client.citizenship || client.residence) && (
            <div className="grid grid-cols-2 gap-2">
              {client.citizenship && <div className="bg-white rounded-2xl p-3.5 border border-slate-100"><div className="text-[11px] text-slate-400 uppercase">Citizenship</div><div className="text-sm font-medium text-slate-800">{client.citizenship}</div></div>}
              {client.status_expiry && <div className="bg-white rounded-2xl p-3.5 border border-slate-100"><div className="text-[11px] text-slate-400 uppercase">Status expiry</div><div className="text-sm font-medium text-slate-800">{prettyDate(client.status_expiry)}</div></div>}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col" style={{ minHeight: "50vh" }}>
          <div className="flex-1 p-4 space-y-3">
            {msgs.length === 0 && <div className="text-center text-sm text-slate-400 py-8">No messages with this client yet.</div>}
            {msgs.map((m) => {
              const fromOffice = !m.to_office;
              return (
                <div key={m.id} className={`flex ${fromOffice ? "flex-row-reverse" : ""}`} data-testid="client-message">
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${fromOffice ? "bg-primary text-white" : "bg-white border border-slate-100 text-slate-800"}`}>
                    <div className="text-[10px] font-semibold opacity-70 mb-0.5">{m.sender_name}{m.channel ? ` · ${m.channel}` : ""}</div>
                    <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                    <div className={`text-[10px] mt-1 ${fromOffice ? "text-white/70" : "text-slate-400"}`}>{timeAgo(m.created_at)}</div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          <div className="border-t border-slate-100 bg-white p-2.5 flex items-end gap-2 sticky bottom-16">
            <textarea data-testid="client-msg-input" rows={1} value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Message to client file" className="flex-1 resize-none max-h-28 px-3.5 py-2.5 rounded-2xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
            <button onClick={send} disabled={sending || !text.trim()} data-testid="client-msg-send" className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center disabled:opacity-40 shrink-0"><Send className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
